import { Badge, Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { InfoCircle, Plus } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { SetupSection } from './SetupSection';
import { describeUtmRows, type UtmRow } from './utils';
import { UtmFinalLinkPreview } from './UtmFinalLinkPreview';
import { UtmParameterRow } from './UtmParameterRow';

interface UtmControlsProps {
  // True while the brand has not edited the convention AND no key is locked by
  // the URL (drives the "Fluenceur convention" badge).
  pristine: boolean;
  // The rows to render (default convention when pristine, else the edited list).
  // Empty means tracking is off.
  parameters: UtmRow[];
  // Convention keys already present in the destination URL (rendered locked).
  lockedKeys: Array<{ key: string; value: string }>;
  previewUrl: string | null;
  // The query actually appended, used to highlight it inside the preview.
  urlTags: string | null;
  onValueChange: (index: number, value: string) => void;
  onKeyChange: (index: number, key: string) => void;
  onAddParameter: () => void;
  onRemoveParameter: (index: number) => void;
  onDisableTracking: () => void;
  onEnableTracking: () => void;
}

/**
 * UTM convention editor for a boost destination. Shows the convention pairs (or
 * the brand's edits), locks any key the URL already carries, previews the final
 * link, and lets the brand add, remove, or clear pairs.
 *
 * Tracking off is an empty list, reachable in one action ("disable") and
 * reversible ("enable" restores the dynamic convention). A locked row stays
 * non-removable on purpose: the pair comes from the brand's own URL, so removing
 * it from this list would not remove it from the destination.
 */
export function UtmControls({
  pristine,
  parameters,
  lockedKeys,
  previewUrl,
  urlTags,
  onValueChange,
  onKeyChange,
  onAddParameter,
  onRemoveParameter,
  onDisableTracking,
  onEnableTracking,
}: UtmControlsProps) {
  const { t } = useTranslation();

  const lockedByKey = new Map(lockedKeys.map((locked) => [locked.key.toLowerCase(), locked.value]));
  const hasLockedKeys = lockedKeys.length > 0;
  const trackingDisabled = parameters.length === 0;
  // Row classification (read-only key by origin, invalid/duplicate key errors).
  const rowMeta = describeUtmRows(parameters);

  return (
    <SetupSection
      title={t('mentions.boost.setup.utm.title')}
      titleAccessory={
        pristine ? (
          <Badge colorPalette="gray" variant="subtle" size="sm">
            {t('mentions.boost.setup.utm.conventionBadge')}
          </Badge>
        ) : undefined
      }
    >
      {trackingDisabled ? (
        <VStack align="stretch" gap={2}>
          <Text fontSize="xs" color="fg.muted" lineHeight="1.5">
            {t('mentions.boost.setup.utm.disabledNote')}
          </Text>
          <Box>
            <Button
              variant="plain"
              size="xs"
              colorPalette="gray"
              px={0}
              gap={1}
              onClick={onEnableTracking}
            >
              <Plus />
              {t('mentions.boost.setup.utm.enableTracking')}
            </Button>
          </Box>
        </VStack>
      ) : (
        <>
          <VStack align="stretch" gap={2}>
            {parameters.map((pair, index) => (
              <UtmParameterRow
                key={pair.id}
                index={index}
                utmKey={pair.key}
                value={pair.value}
                lockedValue={
                  lockedByKey.has(pair.key.trim().toLowerCase())
                    ? (lockedByKey.get(pair.key.trim().toLowerCase()) ?? '')
                    : null
                }
                keyReadOnly={rowMeta[index].keyReadOnly}
                keyError={rowMeta[index].keyError}
                onValueChange={onValueChange}
                onKeyChange={onKeyChange}
                onRemove={onRemoveParameter}
              />
            ))}
          </VStack>

          <HStack gap={4}>
            <Button
              variant="plain"
              size="xs"
              colorPalette="gray"
              px={0}
              gap={1}
              onClick={onAddParameter}
            >
              <Plus />
              {t('mentions.boost.setup.utm.addParameter')}
            </Button>
            <Button
              variant="plain"
              size="xs"
              colorPalette="gray"
              px={0}
              onClick={onDisableTracking}
            >
              {t('mentions.boost.setup.utm.disableTracking')}
            </Button>
          </HStack>

          {previewUrl && <UtmFinalLinkPreview previewUrl={previewUrl} urlTags={urlTags} />}
        </>
      )}

      <HStack gap={1.5} align="flex-start" color="fg.muted">
        <Box mt="1px" display="flex" flexShrink={0}>
          <InfoCircle width={12} height={12} />
        </Box>
        <Text fontSize="2xs" color="fg.muted" lineHeight="1.5">
          {hasLockedKeys
            ? t('mentions.boost.setup.utm.footerNoteLocked')
            : t('mentions.boost.setup.utm.footerNoteDefault')}
        </Text>
      </HStack>
    </SetupSection>
  );
}
